import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import { bindActionCreators } from 'redux';

import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonLists } from 'selectors/currentSalon.selectors';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isTruthy from 'utils/predicates/isTruthy.util';

import { getClientsList } from 'salon/actions';
import SchedulerLoading from 'scheduler/SchedulerLoading.component';
import SchedulerRouter from 'scheduler/SchedulerRouter.component';

const L = logger('SchedulerLoader');

const SchedulerLoader = ({ lists, getClientsList, fetchStylistsBySalon }) => {
  const salonId = useCurrentSalonId();
  const { t } = useTranslation();

  // NOTE: there is a difference between null and []
  const hasRequestedClients = isTruthy(lists?.clients);
  const hasRequestedStylists = isTruthy(lists?.stylists);

  const hasClients = isFullArray(lists?.clients);
  const hasStylists = isFullArray(lists?.stylists);

  const resultClients = useAsync(async () => {
    if (hasRequestedClients || !salonId) return;
    return getClientsList(salonId);
  }, [getClientsList, hasRequestedClients, salonId]);

  const resultStylists = useAsync(async () => {
    if (hasStylists || !salonId) return;
    return fetchStylistsBySalon(salonId);
  }, [fetchStylistsBySalon, hasStylists, salonId]);

  const isLoadingClients = resultClients.loading;
  const isLoadingStylists = resultStylists.loading;

  const dataAttributes = useMemo(
    () => ({
      salonId,
      hasClients,
      hasStylists,
      hasRequestedClients,
      hasRequestedStylists,
      isLoadingClients,
      isLoadingStylists,
    }),
    [salonId, hasClients, hasStylists, hasRequestedClients, hasRequestedStylists, isLoadingClients, isLoadingStylists],
  );

  const message = isLoadingClients
    ? t('scheduler.schedulerLoader.loadingClient')
    : isLoadingStylists
      ? t('scheduler.schedulerLoader.loadingStylist')
      : hasStylists
        ? t('scheduler.schedulerLoader.loadingData')
        : t('scheduler.schedulerLoader.noStylist');

  L.debug('()', message);

  return hasRequestedClients && hasRequestedStylists && !isLoadingClients && !isLoadingStylists && hasStylists ? (
    <SchedulerRouter dataAttributes={dataAttributes} />
  ) : (
    <SchedulerLoading dataAttributes={dataAttributes} message={message} />
  );
};

const SchedulerLoaderHoc = connect(
  (state) => ({
    lists: selectCurrentSalonLists(state),
  }),
  (dispatch) => bindActionCreators({ getClientsList, fetchStylistsBySalon }, dispatch),
)(SchedulerLoader);

export default SchedulerLoaderHoc;
